import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Save, Printer, FileText } from "lucide-react";
import { api, eur, dt, errMsg, MODALITA, download } from "@/lib/api";
import { MailButton } from "@/components/MailButton";
import { useAuth, canWrite, isAgent } from "@/context/AuthContext";
import { PageHeader, Panel, Field, Empty } from "@/components/Common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CustomerDialog, EMPTY_CUSTOMER, TAB_LIST, TAB_TRIGGER } from "@/components/QuickDialogs";

const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const ANNI = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);
const Info = ({ l, v }) => <div><div className="text-xs uppercase tracking-wide text-slate-500">{l}</div><div className="text-sm font-medium text-slate-900">{v || "\u2014"}</div></div>;
const Anno = ({ anno, setAnno, testid }) => (
  <Select value={String(anno)} onValueChange={(v) => setAnno(Number(v))}>
    <SelectTrigger data-testid={testid} className="w-28 bg-white"><SelectValue /></SelectTrigger>
    <SelectContent>{ANNI.map((a) => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}</SelectContent>
  </Select>
);

const Anagrafica = ({ c }) => (
  <div className="grid gap-5 sm:grid-cols-3">
    <Info l="Codice" v={c.codice} /><Info l="Tipo" v={c.tipo} /><Info l="Partita IVA" v={c.piva} />
    <Info l="Codice fiscale" v={c.codice_fiscale} /><Info l="Indirizzo" v={`${c.indirizzo || ""} ${c.cap || ""} ${c.comune || ""} ${c.provincia ? `(${c.provincia})` : ""}`} /><Info l="Nazione" v={c.nazione} />
    <Info l="Telefono" v={c.telefono} /><Info l="Email" v={c.email} /><Info l="Note" v={c.note} />
  </div>
);

const FatturaEl = ({ c }) => (
  <div className="grid gap-5 sm:grid-cols-3">
    <Info l="Destinatario" v={c.pa || c.codice_destinatario?.length === 6 ? "Pubblica amministrazione (split payment)" : "Azienda privata (B2B/B2C)"} />
    <Info l="Codice destinatario" v={c.codice_destinatario} /><Info l="PEC" v={c.pec} />
    <p className="text-xs text-slate-500 sm:col-span-3">PA: codice univoco ufficio di 6 caratteri. Privati: codice di 7 caratteri o 0000000 con PEC. Esteri: XXXXXXX.</p>
  </div>
);

const Rapporti = ({ c, onSaved, ro }) => {
  const [f, setF] = useState(c);
  const [agenti, setAgenti] = useState([]);
  useEffect(() => { setF(c); }, [c]);
  useEffect(() => { if (!ro) api.get("/users").then((r) => setAgenti(r.data.filter((u) => u.tenant_role === "agente"))).catch(() => {}); }, [ro]);
  const save = async () => {
    try { await api.put(`/customers/${c.id}`, { ...EMPTY_CUSTOMER, ...f, fido: Number(f.fido) || 0 }); toast.success("Rapporti commerciali salvati"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const inp = (k, l, p = {}) => <Field label={l}><Input disabled={ro} data-testid={`cc-${k}`} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} {...p} /></Field>;
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {inp("sconto", "Sconti (es. 10+5)")}{inp("fido", "Fido \u20ac", { type: "number", step: "0.01" })}
      <Field label="Agente">
        <Select disabled={ro} value={f.agente_id || "none"} onValueChange={(v) => setF({ ...f, agente_id: v === "none" ? "" : v })}>
          <SelectTrigger data-testid="cc-agente"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="none">Nessun agente</SelectItem>{agenti.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}{f.agente_id && !agenti.find((a) => a.id === f.agente_id) && <SelectItem value={f.agente_id}>Agente assegnato</SelectItem>}</SelectContent>
        </Select>
      </Field>
      <Field label="Modalità di pagamento">
        <Select disabled={ro} value={f.modalita_pagamento || "none"} onValueChange={(v) => setF({ ...f, modalita_pagamento: v === "none" ? "" : v })}>
          <SelectTrigger data-testid="cc-pagamento"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="none">Non indicata</SelectItem>{Object.entries(MODALITA).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      {inp("iban", "Coordinate bancarie cliente (IBAN)")}{inp("banca_ditta", "IBAN banca ditta (dove il cliente paga)")}
      {inp("trasporto", "Incaricato del trasporto")}{inp("porto", "Porto (franco, assegnato\u2026)")}
      {!ro && <div className="sm:col-span-2"><Button className="bg-sky-600 hover:bg-sky-700" data-testid="cc-save" onClick={save}><Save className="mr-2 h-4 w-4" />Salva</Button></div>}
    </div>
  );
};

const Scheda = ({ id }) => {
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/customers/${id}/scheda`, { params: { anno } }).then((r) => setD(r.data)); }, [id, anno]);
  return (
    <div>
      <div className="mb-3 flex items-center gap-3"><Anno anno={anno} setAnno={setAnno} testid="cc-scheda-year" /><Button variant="outline" size="sm" className="bg-white" onClick={() => window.print()}><Printer className="mr-1 h-4 w-4" />Stampa</Button></div>
      {d && (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm" data-testid="cc-scheda-table">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="p-2">Data</th><th>Documento</th><th>Data doc.</th><th>Tipo</th><th>Causale</th><th className="text-right">Dare</th><th className="text-right">Avere</th><th className="p-2 text-right">Saldo</th></tr></thead>
            <tbody>
              <tr className="bg-sky-50"><td className="p-2" colSpan={5}>Saldo anno precedente ({anno - 1})</td><td /><td /><td className="p-2 text-right font-mono font-semibold">{eur(d.saldo_precedente)}</td></tr>
              {d.movimenti.map((m, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="p-2">{dt(m.data)}</td><td><Link to={`/app/fatture/${m.invoice_id}`} className="font-mono text-sky-700 hover:underline">{m.numero}</Link></td><td>{dt(m.data_doc)}</td><td>{m.tipo}</td><td>{m.causale}</td>
                  <td className="text-right font-mono">{m.dare ? eur(m.dare) : ""}</td><td className="text-right font-mono">{m.avere ? eur(m.avere) : ""}</td><td className="p-2 text-right font-mono">{eur(m.saldo)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-300 bg-amber-50 font-semibold" data-testid="cc-scheda-total"><td className="p-2" colSpan={5}>Totale</td><td className="text-right font-mono">{eur(d.totale_dare)}</td><td className="text-right font-mono">{eur(d.totale_avere)}</td><td className="p-2 text-right font-mono">{eur(d.saldo)}</td></tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const Report = ({ id }) => {
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/customers/${id}/report`, { params: { anno } }).then((r) => setD(r.data)); }, [id, anno]);
  const max = Math.max(1, ...(d?.mesi || []).map((m) => Math.abs(m.imponibile)));
  return (
    <div>
      <div className="mb-4 flex items-center gap-4"><Anno anno={anno} setAnno={setAnno} testid="cc-report-year" />{d && <span className="text-sm">Imponibile <b className="font-mono">{eur(d.imponibile)}</b> · Totale <b className="font-mono">{eur(d.totale)}</b></span>}</div>
      {d && <div className="flex h-48 items-end gap-2" data-testid="cc-report-chart">{d.mesi.map((m) => (
        <div key={m.mese} className="flex flex-1 flex-col items-center gap-1" title={`${eur(m.imponibile)} · ${m.documenti} documenti`}>
          <div className="w-full rounded-t bg-sky-500" style={{ height: `${(Math.abs(m.imponibile) / max) * 160}px` }} /><span className="text-xs text-slate-600">{MESI[m.mese - 1]}</span>
        </div>))}</div>}
    </div>
  );
};

const Email = ({ id }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/customers/${id}/emails`).then((r) => setD(r.data)); }, [id]);
  if (!d) return null;
  return d.length === 0 ? <Empty text="Nessuna email inviata a questa anagrafica" /> : (
    <table className="w-full text-sm" data-testid="cc-email-table">
      <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="p-2">Data invio</th><th>Inviata da</th><th>Destinatario</th><th>Oggetto</th></tr></thead>
      <tbody>{d.map((e, i) => <tr key={i} className="border-t border-slate-100"><td className="p-2">{new Date(e.at).toLocaleString("it-IT")}</td><td>{e.by}</td><td>{e.to}</td><td>{e.oggetto}</td></tr>)}</tbody>
    </table>
  );
};

export default function CustomerCard() {
  const { id } = useParams();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [edit, setEdit] = useState(null);
  const ro = !canWrite(user) || isAgent(user);
  const load = useCallback(() => api.get("/customers").then((r) => setC(r.data.find((x) => x.id === id) || false)), [id]);
  useEffect(() => { load(); }, [load]);
  if (c === null) return null;
  if (!c) return <Empty text="Anagrafica non trovata" />;
  return (
    <div>
      <PageHeader title={c.denominazione || `${c.nome} ${c.cognome}`} subtitle={`${c.codice ? `Codice ${c.codice} · ` : ""}${c.piva ? `P.IVA ${c.piva}` : c.codice_fiscale || ""}`}>
        <Button asChild variant="outline" className="bg-white"><Link to="/app/anagrafiche"><ArrowLeft className="mr-2 h-4 w-4" />Elenco</Link></Button>
        {<Button variant="outline" className="bg-white" data-testid="cc-estratto-pdf" onClick={() => download(`/customers/${id}/estratto.pdf`).catch((e) => toast.error(e.message))}><FileText className="mr-2 h-4 w-4" />Estratto conto</Button>}
        {!ro && <MailButton path={`/customers/${id}/estratto/email`} email={c.email} what="l'estratto conto" label="Invia estratto conto" testid="cc-estratto-email" />}
        {!ro && <Button className="bg-sky-600 hover:bg-sky-700" data-testid="cc-edit" onClick={() => setEdit({ ...EMPTY_CUSTOMER, ...c })}><Pencil className="mr-2 h-4 w-4" />Modifica anagrafica</Button>}
      </PageHeader>
      <Tabs defaultValue="anagrafica">
        <TabsList className={TAB_LIST}>
          {[["anagrafica", "Dati anagrafici"], ["fe", "Fattura elettronica"], ["rapporti", "Rapporti commerciali"], ["scheda", "Scheda contabile"], ["report", "Reportistica"], ["email", "E-mail"]].map(([k, l]) => <TabsTrigger className={TAB_TRIGGER} key={k} value={k} data-testid={`cc-tab-${k}`}>{l}</TabsTrigger>)}
        </TabsList>
        <Panel className="mt-4">
          <TabsContent value="anagrafica"><Anagrafica c={c} /></TabsContent>
          <TabsContent value="fe"><FatturaEl c={c} /></TabsContent>
          <TabsContent value="rapporti"><Rapporti c={c} ro={ro} onSaved={load} /></TabsContent>
          <TabsContent value="scheda"><Scheda id={id} /></TabsContent>
          <TabsContent value="report"><Report id={id} /></TabsContent>
          <TabsContent value="email"><Email id={id} /></TabsContent>
        </Panel>
      </Tabs>
      <CustomerDialog value={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
